import type { ScaleName } from 'Types/ExerciseContentTypes';
import { PLAYABLE_SCALE_UNIT_IDS, unitIdForScaleSlug } from 'Seeds/ExerciseContent/scaleLevels';
import {
  waypointConfig,
  type ExerciseContent,
  type ExerciseContentLevel,
} from './exerciseContentApi';
import type {
  ExercisePrePracticeReadModel,
  PrePracticeClassification,
  PrePracticeLevel,
} from './prePracticeReadModel';

/**
 * The overview's read model from the server's exercise read (`GET /exercises/:slug`): the
 * exercise's id, title, levels, cells and tiers as the server holds them.
 *
 * Levels are shown in the scale catalog's order (family, then derivation order), never in
 * `level_number` order: a level number is a permanent identity assigned when a level is first
 * written, so a scale added to a family later takes a high number but still shows inside its
 * family.
 */

/** The slug of the one exercise the section plays today: "Play Scales from Notation". */
export const SCALES_EXERCISE_SLUG = 'scales-performance-read';

/**
 * How the scales exercise is classified. The read does not serve the classification labels, so
 * the one exercise's labels are held here until it does.
 */
export const SCALES_EXERCISE_CLASSIFICATION: PrePracticeClassification = {
  topicLabel: 'Scales & Keys',
  approachLabel: 'by playing',
  contentClassCode: 'BL-020',
  sessionPatternCode: 'SP-03',
};

/** A level's place in the catalog: its scale's position in display order; unknown scales last. */
function catalogPosition(level: ExerciseContentLevel): number {
  const scale = level.cells[0]?.scale;
  const position = scale
    ? PLAYABLE_SCALE_UNIT_IDS.indexOf(unitIdForScaleSlug(scale as ScaleName))
    : -1;
  return position >= 0 ? position : PLAYABLE_SCALE_UNIT_IDS.length;
}

/** The levels in display order: catalog order, then level number for any the catalog lacks. */
export function levelsInCatalogOrder(
  levels: readonly ExerciseContentLevel[],
): ExerciseContentLevel[] {
  return [...levels].sort(
    (a, b) => catalogPosition(a) - catalogPosition(b) || a.level_number - b.level_number,
  );
}

function toPrePracticeLevel(level: ExerciseContentLevel): PrePracticeLevel {
  return {
    levelNumber: level.level_number,
    title: level.title,
    isReview: level.is_review,
    description: null,
    // A tier whose waypoint leaves a scale dimension unset cannot be played: not shown.
    tiers: [...level.tiers]
      .sort((a, b) => a.tier_number - b.tier_number)
      .flatMap((tier) => {
        const config = waypointConfig(tier.waypoint);
        return config
          ? [
              {
                tierNumber: tier.tier_number,
                hand: config.hand,
                rangeOctaves: config.range_octaves,
                direction: config.direction,
                rhythmDensity: config.rhythm_density,
                tempo: config.tempo,
              },
            ]
          : [];
      }),
    cells: level.cells.map(({ scale, tonic }) => ({ scale, tonic })),
  };
}

/** The overview's read model for an exercise the server served. */
export function readModelFromContent(content: ExerciseContent): ExercisePrePracticeReadModel {
  return {
    exerciseId: content.id,
    slug: content.slug,
    title: content.title,
    description: null,
    classification: SCALES_EXERCISE_CLASSIFICATION,
    levels: levelsInCatalogOrder(content.levels).map(toPrePracticeLevel),
  };
}
